/**
 * Payment Client — PAYG prepaid balance (ADR 0002).
 *
 * Thin wrappers over the worker payment routes. openCheckout POSTs a top-up
 * pack label ('5'|'10'|'20'), receives a Stripe hosted-page URL, and redirects.
 * On any failure (non-2xx, network, missing url) it throws — callers render an
 * inline error in the surface the user clicked (balance-card, Settings card).
 *
 * The client sends NO priceId: the server owns the Stripe Price (ADR-0001).
 */

import { API_URL } from '@/env'
import { buildAuthHeaders } from './auth-client'

const DEFAULT_BASE_URL = API_URL || (typeof window !== 'undefined' ? window.location.origin : '')

/** Balance meter, as returned by GET /status. balanceCents is the prepaid
 *  energy (1 cent = 1 energy); costCents is the per-card-gen price; imageCostCents
 *  is the per-AI-image-regen price. Both costs let the client show prices up-front
 *  and pre-empt the buttons when balance < cost. */
export interface PaymentStatus {
  balanceCents: number
  costCents: number
  imageCostCents?: number
  /** Has the user ever paid? Drives the Premium gate on AI image regen. */
  hasPaid?: boolean
}

/** Top-up pack labels the server resolves to a Stripe Price ($5/$10/$20). */
export type TopupAmount = '5' | '10' | '20'

/** A Stripe hosted-page URL returned by /checkout. */
interface PaymentUrlResponse {
  url?: string
  error?: string
}

// ponytail: dedup rageclicks on a top-up — one in-flight checkout session, one
// redirect. Concurrent calls share the same promise.
let pendingRedirect: Promise<void> | null = null

async function postPaymentRoute(path: string, body: unknown): Promise<void> {
  if (pendingRedirect) return pendingRedirect
  pendingRedirect = runPaymentRoute(path, body)
  try {
    await pendingRedirect
  } finally {
    pendingRedirect = null
  }
}

async function runPaymentRoute(path: string, body: unknown): Promise<void> {
  const response = await fetch(`${DEFAULT_BASE_URL}${path}`, {
    method: 'POST',
    headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    credentials: 'include',
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({} as PaymentUrlResponse))
    throw new Error(data.error || `HTTP ${response.status}`)
  }

  const data = (await response.json()) as PaymentUrlResponse
  if (!data.url) throw new Error('Payment response missing url')

  window.location.href = data.url
}

/**
 * Fetch the balance meter (GET /status). Drives PaymentContext.
 * Throws on non-2xx; the context swallows it (gate backstops).
 */
export async function getPaymentStatus(): Promise<PaymentStatus> {
  const response = await fetch(`${DEFAULT_BASE_URL}/api/payment/status`, {
    headers: buildAuthHeaders(),
    credentials: 'include',
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return (await response.json()) as PaymentStatus
}

/**
 * Start a Stripe Checkout session for a top-up pack and redirect to it.
 * Throws on failure; the caller shows an inline error.
 */
export async function openCheckout(amount: TopupAmount): Promise<void> {
  await postPaymentRoute('/api/payment/checkout', { amount })
}
